import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import {
  useGetCommunities,
  useCommunityId,
  useGetCommunityCollections,
  type Community,
} from '@/hooks/useCommunities';
import { isFreshLaunch, storeAuthDate } from '@/utils/launchParams';
import { useQueryClient } from '@tanstack/react-query';
import { type SupportedCollection } from '../hooks/useCollections';

interface SelectedCommunityProviderType {
  selectedCommunity: Community | null;
  availableCommunities: Community[];
  setSelectedCommunity: (community: Community | null) => void;
  isLoading: boolean;
  isRefetching: boolean;
  error: Error | null;
  defaultCollection?: SupportedCollection;
}

const SelectedCommunityContext = createContext<
  SelectedCommunityProviderType | undefined
>(undefined);

const STORAGE_KEY = 'selectedCommunity';

export function SelectCommunityProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [selectedCommunity, setSelectedCommunityState] =
    useState<Community | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [communityIdToRefetch, setCommunityIdToRefetch] = useState<
    string | undefined
  >(undefined);
  const [localError, setLocalError] = useState<Error | null>(null);

  // Get communityId and authDate from URL (priority)
  const { communityId: communityIdFromUrl, authDate } = useCommunityId();

  // Fetch community from URL if present
  const {
    data: communityFromUrl,
    isLoading: isLoadingFromUrl,
    error: communityFromURLError,
  } = useGetCommunityCollections(communityIdFromUrl);

  // Fetch all available communities
  const { data: availableCommunities, isLoading, error } = useGetCommunities();

  // Refetch community loaded from localStorage to get latest data
  const { data: refetchedCommunity, isLoading: isRefetching } =
    useGetCommunityCollections(communityIdToRefetch);

  const defaultCollection = selectedCommunity?.collections.filter(
    t => t.id == selectedCommunity.default_collection_id?.toString()
  )[0]!;

  // Initialize from URL (priority) or localStorage
  useEffect(() => {
    // Priority 0: Auto-select if only 1 community available
    // (Check this BEFORE hasInitialized to ensure it runs when communities load)
    if (
      !isLoading &&
      availableCommunities.length === 1 &&
      !selectedCommunity &&
      !communityIdFromUrl
    ) {
      const singleCommunity = availableCommunities[0];
      setSelectedCommunityState(singleCommunity);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(singleCommunity));
      setHasInitialized(true);
      return;
    }

    if (hasInitialized) return;

    // Priority 1: URL communityId - but check if it's a fresh launch
    if (communityIdFromUrl) {
      // Check if this is a fresh launch or a page reload
      const isNewLaunch = isFreshLaunch(authDate);

      // Only process communityId if it's a fresh launch
      if (isNewLaunch) {
        if (communityFromURLError) {
          // Set error message and mark as initialized to trigger community selection UI
          setLocalError(
            new Error(
              'Community not found. Please select one from the available communities.'
            )
          );
          setHasInitialized(true);
          return;
        }
        if (communityFromUrl && !isLoadingFromUrl) {
          setSelectedCommunityState(communityFromUrl);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(communityFromUrl));

          // Store the auth_date to detect future reloads
          storeAuthDate(authDate);

          setHasInitialized(true);

          // Clean up URL parameter
          if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('communityId');
            window.history.replaceState({}, '', url.toString());
          }
        }
        return; // Wait for fetch or skip localStorage check
      } else {
        // Not a fresh launch (reload) - ignore URL communityId and use localStorage
        // Fall through to localStorage loading below
      }
    }

    // Priority 2: localStorage (only if no URL communityId or reload detected)
    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (stored) {
        const parsedCommunity = JSON.parse(stored) as Community;
        setSelectedCommunityState(parsedCommunity);
        // Trigger refetch to get latest community data
        setCommunityIdToRefetch(parsedCommunity.id);
        setHasInitialized(true);
      }
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
    }

    // Only mark as initialized if communities have finished loading
    // This ensures Priority 0 (auto-select single community) can run when data loads
    if (!isLoading) {
      setHasInitialized(true);
    }
  }, [
    communityIdFromUrl,
    communityFromUrl,
    isLoadingFromUrl,
    hasInitialized,
    authDate,
    availableCommunities,
    isLoading,
    selectedCommunity,
  ]);

  // Handle refetched community data - update state and localStorage with fresh data
  useEffect(() => {
    if (refetchedCommunity && !isRefetching && communityIdToRefetch) {
      // Update state with fresh data
      setSelectedCommunityState(refetchedCommunity);

      // Save fresh data to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(refetchedCommunity));

      // Clear the refetch trigger to prevent re-fetching
      setCommunityIdToRefetch(undefined);
    }
  }, [refetchedCommunity, isRefetching, communityIdToRefetch]);

  // Wrapper to persist to localStorage whenever community changes
  const setSelectedCommunity = (community: Community | null) => {
    setSelectedCommunityState(community);

    if (community) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(community));
      // Clear any previous errors when successfully selecting a community
      setLocalError(null);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }

    // Invalidate all queries when community changes
    queryClient.invalidateQueries();
  };

  // Calculate ready state: true when initialization is complete and no refetch pending
  const isReady = hasInitialized && !isRefetching && !communityIdToRefetch;

  // Determine if we have any community data
  const hasAnyCommunityData = !!selectedCommunity;

  return (
    <SelectedCommunityContext.Provider
      value={{
        selectedCommunity,
        availableCommunities,
        setSelectedCommunity,
        isLoading: !hasAnyCommunityData && (isLoading || !isReady),
        isRefetching: hasAnyCommunityData && (isLoadingFromUrl || isRefetching),
        error: localError || error,
        defaultCollection,
      }}
    >
      {children}
    </SelectedCommunityContext.Provider>
  );
}

export function useSelectCommunity() {
  const context = useContext(SelectedCommunityContext);
  if (context === undefined) {
    throw new Error(
      'useSelectCommunity must be used within a SelectCommunityProvider'
    );
  }
  return context;
}
